import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Chip, CircularProgress, Button } from '@mui/material';
import { getRooms } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import RoomImageCarousel from './RoomImageCarousel';

const AUTO_SLIDE_INTERVAL_MS = 4000;

const RoomCardImageSlider = ({ room, height = 200, onClick }) => {
  const imgs = room?.imageUrls?.length ? room.imageUrls : (room?.imageUrl ? [room.imageUrl] : []);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (imgs.length <= 1) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % imgs.length);
    }, AUTO_SLIDE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [imgs.length]);

  if (imgs.length === 0) {
    return (
      <Box
        sx={{
          height,
          bgcolor: 'var(--color-sand)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ color: 'var(--color-warm-gray)', fontSize: '3rem', opacity: 0.4 }}>🛏️</Typography>
      </Box>
    );
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        height,
        bgcolor: 'var(--color-sand)',
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'opacity 0.2s',
        '&:hover': onClick ? { opacity: 0.95 } : {},
      }}
    >
      {imgs.map((src, i) => (
        <Box
          key={i}
          component="img"
          src={src}
          alt={`${room?.name} ${i + 1}`}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: i === idx ? 1 : 0,
            transition: 'opacity 0.6s ease-in-out',
            pointerEvents: 'none',
          }}
        />
      ))}
      {imgs.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: 0.5,
          }}
        >
          {imgs.map((_, i) => (
            <Box
              key={i}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: i === idx ? 'white' : 'rgba(255,255,255,0.4)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const RoomCard = ({ room, onImageClick }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'var(--color-sand)',
      overflow: 'hidden',
      height: '100%',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 40px rgba(44,41,38,0.08)',
      },
    }}
  >
    <RoomCardImageSlider
      room={room}
      height={200}
      onClick={(room.imageUrl || room.imageUrls?.length) ? () => onImageClick?.(room) : undefined}
    />
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography
          variant="h5"
          sx={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}
        >
          {room.name}
        </Typography>
        {room.rent > 0 && (
          <Typography sx={{ fontWeight: 600, color: 'var(--color-accent)' }}>
            {formatCurrency(room.rent)}
            <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--color-warm-gray)' }}> /night</Typography>
          </Typography>
        )}
      </Box>
      {room.capacity > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={`Up to ${room.capacity} guests`} size="small" sx={{ borderRadius: 1 }} />
        </Box>
      )}
      {room.notes && (
        <Typography sx={{ mt: 1.5, fontSize: '0.9rem', color: 'var(--color-warm-gray)', lineHeight: 1.6 }}>
          {room.notes}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const RoomsSection = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carouselRoom, setCarouselRoom] = useState(null);

  useEffect(() => {
    getRooms()
      .then((res) => {
        const data = res.data;
        const list = Array.isArray(data) ? data : (data?.rooms ?? data?.data ?? []);
        const arr = Array.isArray(list) ? list : [];
        setRooms(arr.filter((r) => r && r.isActive !== false));
      })
      .catch(() => {
        setRooms([]);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box
      id="rooms"
      sx={{
        py: { xs: 8, md: 10 },
        px: 3,
        bgcolor: 'white',
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography
          variant="overline"
          sx={{ letterSpacing: 3, color: 'var(--color-warm-gray)', fontWeight: 500, fontSize: '0.85rem' }}
        >
          Accommodation
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: { xs: '2.25rem', md: '3rem' },
            fontWeight: 500,
            mt: 1,
            mb: 2,
            color: 'var(--color-charcoal)',
          }}
        >
          Rooms &amp; Availability
        </Typography>
        <Typography
          component="p"
          sx={{
            color: 'var(--color-warm-gray)',
            fontSize: '1rem',
            lineHeight: 1.75,
            maxWidth: 720,
            mb: 4,
          }}
        >
          Stay in <strong>clean, well-kept rooms</strong> when you visit{' '}
          <strong>Valparai or Sholayar Dam</strong> — whether you are comparing{' '}
          <strong>hotels, resorts, or homestays in the Valparai area</strong>, our listings update
          here for <strong>real-time room availability</strong> and nightly rates.
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: 'var(--color-accent)' }} />
          </Box>
        ) : rooms.length === 0 ? (
          <Typography sx={{ textAlign: 'center', color: 'var(--color-warm-gray)', py: 6 }}>
            No rooms available at the moment.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {rooms.map((room) => (
              <RoomCard key={room._id} room={room} onImageClick={setCarouselRoom} />
            ))}
          </Box>
        )}
        {!loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              component={Link}
              to="/book"
              variant="contained"
              size="large"
              sx={{
                py: 1.5,
                px: 3,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                bgcolor: 'var(--color-accent)',
                '&:hover': { bgcolor: 'var(--color-accent-light)' },
              }}
            >
              Book
            </Button>
          </Box>
        )}
        <RoomImageCarousel room={carouselRoom} open={!!carouselRoom} onClose={() => setCarouselRoom(null)} />
      </Box>
    </Box>
  );
};

export default RoomsSection;
